import React, { useState, useEffect } from 'react';
import useAttendanceRecordsLogic from 'hooks/useAttendance';
import AdminNavigation from 'layouts/adminNavigation';
import Icon from '../../components/appIcon';
import Button from '../../components/ui/button';
import AttendanceEditModal from './components/attendanceEditModal';
import AttendanceFilters from './components/attendanceFilters';
import AttendanceMobileCard from './components/attendanceMobileCard';
import AttendancePagination from './components/attendancePagination';
import AttendanceStats from './components/attendanceStats';
import AttendanceTable from './components/attendanceTable';
import { useAuth } from 'components/ui/navigationGuard';
import DeleteConfirmationModal from './components/deleteConfirmationModal';
import EmployeeAttendanceModal from './components/employeeAttendanceModal';
import EmployeeList from './components/employeeList';
import SwitchPersonModal from './components/switchPersonModal';

const AttendanceRecords = () => {
  // Use the custom hook to get all the state and functions
  const {
    filteredRecords,
    selectedRecords,
    currentPage,
    recordsPerPage,
    sortConfig,
    isLoading,
    isMobile,
    sidebarCollapsed,
    isUnknownFilter,
    editingRecord,
    isEditModalOpen,
    recordToDelete,
    isDeleteModalOpen,
    activeTab,
    employees,
    timeFilter,
    isEmployeeAttendanceModalOpen,
    selectedEmployeeForAttendance,
    employeeAttendanceSummary,
    employeeAttendanceRecords,
    isSwitchModalOpen,
    recordToSwitch,
    setIsUnknownFilter,
    setIsEditModalOpen,
    setIsDeleteModalOpen,
    setIsEmployeeAttendanceModalOpen,
    fetchAttendance,
    getStats,
    getCurrentPageRecords,
    handleFiltersChange,
    handleSort,
    handleSelectRecord,
    handleSelectAll,
    handleBulkAction,
    handleExport,
    handlePageChange,
    handleRecordsPerPageChange,
    handleToggleCollapse,
    handleEditClick,
    handleDeleteClick,
    handleUpdateRecord,
    handleConfirmDelete,
    handleTabChange,
    handleTimeFilterChange,
    handleViewEmployeeAttendance,
    handleSwitchClick,
    handleCloseSwitchModal,
    user,
    totalPages,
    detectionMode,
    employeeFilters,
    handleEmployeeFilterChange,
    employeeSummary,
  summaryMonth,
  summaryYear,
  fetchEmployeeSummary,
  handleSummaryMonthChange,
  handleSummaryYearChange,
  } = useAttendanceRecordsLogic();

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation
          user={user}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={handleToggleCollapse}
        />
        <div className={`${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'} pt-16 transition-all duration-200`}>
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading attendance records...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation
        user={user}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleCollapse}
      />

      <div className={`${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'} pt-16 transition-all duration-200`}>
        {/* Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-4">
                <h1 className="text-2xl font-bold text-foreground">Attendance Records</h1>
                <div className="hidden sm:flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Calendar" size={16} />
                  <span>
                    Today:{" "}
                    {new Date()?.toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="default"
                  onClick={fetchAttendance}
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Refresh
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Stats */}
          {/* <AttendanceStats stats={getStats()} /> */}

          {/* Filters */}
          <AttendanceFilters
            onFiltersChange={handleFiltersChange}
            onExport={handleExport}
            totalRecords={filteredRecords?.length}
            isUnknownFilter={isUnknownFilter}
            onIsUnknownFilterChange={setIsUnknownFilter}
            activeTab={activeTab}
            handleTimeFilterChange={handleTimeFilterChange}
            timeFilter={timeFilter}
          />

          {/* Tabs */}
          <div className="mb-6">
            <div className="border-b border-border">
              <nav className="-mb-px flex space-x-8" role="tablist">
                <button
                  onClick={() => handleTabChange('all')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'all'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  role="tab"
                  aria-selected={activeTab === 'all'}
                >
                  All Records
                </button>
                <button
                  onClick={() => handleTabChange('employee')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'employee'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  role="tab"
                  aria-selected={activeTab === 'employee'}
                >
                  Employee-wise
                </button>
              </nav>
            </div>
          </div>

          {/* Employee List or Table / Mobile Cards */}
          {activeTab === 'employee' ? (
            <EmployeeList
              employees={employees}
              onViewEmployee={handleViewEmployeeAttendance}
              onFilterChange={handleEmployeeFilterChange}
              employeeSummary={employeeSummary}
              summaryMonth={summaryMonth}
              summaryYear={summaryYear}
              onSummaryMonthChange={handleSummaryMonthChange}
              onSummaryYearChange={handleSummaryYearChange}
              onFetchEmployeeSummary={fetchEmployeeSummary}
            />
          ) : isMobile ? (
            <div className="space-y-4 mb-6">
              {getCurrentPageRecords()?.map((record) => (
                <AttendanceMobileCard
                  key={record?.id}
                  record={record}
                  onSelect={handleSelectRecord}
                  isSelected={selectedRecords?.includes(record?.id)}
                  showEmployeeInfo
                />
              ))}
            </div>
          ) : (
            <div className="mb-6">
              <AttendanceTable
                records={getCurrentPageRecords()}
                onSort={handleSort}
                sortConfig={sortConfig}
                onBulkAction={handleBulkAction}
                selectedRecords={selectedRecords}
                onSelectRecord={handleSelectRecord}
                onSelectAll={handleSelectAll}
                showEmployeeColumn={activeTab === 'all'}
                onEditClick={handleEditClick}
                onDeleteClick={handleDeleteClick}
                onViewClick={null}
                onSwitchClick={handleSwitchClick}
                detectionMode={detectionMode}
              />
            </div>
          )}
          {isSwitchModalOpen && recordToSwitch && (
            <SwitchPersonModal
              record={recordToSwitch}
              onClose={handleCloseSwitchModal}
              onUpdate={fetchAttendance}
            />
          )}
          {/* Pagination */}
          {activeTab === 'all' && (
            <AttendancePagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={filteredRecords?.length}
              recordsPerPage={recordsPerPage}
              onPageChange={handlePageChange}
              onRecordsPerPageChange={handleRecordsPerPageChange}
            />
          )}

          {/* Edit Modal */}
          {isEditModalOpen && (
            <AttendanceEditModal
              record={editingRecord}
              onClose={() => setIsEditModalOpen(false)}
              onSave={handleUpdateRecord}
            />
          )}

          {/* Delete Confirmation Modal */}
          {isDeleteModalOpen && (
            <DeleteConfirmationModal
              record={recordToDelete}
              onClose={() => setIsDeleteModalOpen(false)}
              onConfirm={handleConfirmDelete}
            />
          )}

          {/* Employee Attendance Modal */}
          {isEmployeeAttendanceModalOpen && selectedEmployeeForAttendance && (
            <EmployeeAttendanceModal
              employee={selectedEmployeeForAttendance}
              attendanceSummary={employeeAttendanceSummary}
              attendanceRecords={employeeAttendanceRecords}
              timeFilter={timeFilter}
              onClose={() => setIsEmployeeAttendanceModalOpen(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceRecords;